<script setup lang="ts">
import type {ProjectData} from "~/types/ProjectData";
import {formatReadingTime, nl2br} from "~/utils/format";
import {UBadge} from "#components";
import type {CommentData} from "~/types/CommentData";

import {chaptersCountLabel, projectStatusLabel} from "~/utils/project";
import {$api} from "~/composables/useApiFetch";
import {userHasRole} from "~/utils/user";
import {useMyFavorites} from "~/composables/useMyFavorites";
import type {ApiError} from "~/types/ApiError";
import SupportProjectCard from "~/components/SupportProjectCard.vue";
import CollaborationRequestCard from "~/components/CollaborationRequestCard.vue";
import {spiceLevelLabel} from "~/composables/useSpiceLevels";
import Tooltip from "~/components/Tooltip.vue";
import ShareProject from "~/components/ShareProject.vue";
import {computed} from "vue";

const props = defineProps<{
  refreshNuxtData?: () => void
}>();

const route = useRoute();
const router = useRouter();
const appUser = useAppUser();
const {handleProjectFavorite} = useMyFavorites();

const projectUuid = route.params.project;
if (typeof projectUuid !== "string") {
  throw new Error("UUID d'histoire invalide");
}

const isReader = computed(() => !!appUser.value && userHasRole(appUser.value, "reader"));
const isAuthor = computed(() => !!appUser.value && userHasRole(appUser.value, "author"));
const canRead = computed(() => isReader.value || isAuthor.value);

// Fetch data
const {data: projectResponse, error: projectError} = await useAsyncData(
    `project:${projectUuid}`,
    () => $api<{ data: ProjectData }>(`/api/project/${projectUuid}`),
);

if (projectError.value) {
  const errorData = projectError.value.data as ApiError;
  throw createError({
    statusCode: projectError.value.statusCode || 500,
    statusMessage: errorData?.message || "Histoire introuvable",
    fatal: true,
  });
}

const project = computed<ProjectData | null>(() => projectResponse.value?.data ?? null);

const config = useRuntimeConfig();
const projectUrl = computed(() => {
  if (!project.value) return "";
  return `${config.public.siteUrl}/project/${project.value.uuid}`;
});

const isTheAuthor = computed(() => {
  if (!appUser.value || !project.value) return false;
  return project.value.authors.some(author => author.uuid === appUser.value?.uuid);
});

const formattedTitle = computed(() => {
  const title = project.value?.title || "Histoire";
  const authorNames = project.value?.authors?.map(a => a.name).join(", ");
  return authorNames ? `${title} - ${authorNames}` : title;
});

useWreadiaSeo({
  title: () => formattedTitle.value,
  description: () => project.value?.pitch || project.value?.abstract,
  image: () => project.value?.image?.original,
  ogType: "book",
  extra: {
    bookAuthor: () => project.value?.authors?.map(author => author.name),
  }
});

if (!project.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Histoire introuvable",
    fatal: true,
  });
}

const bookmark = computed(() => project.value?.last_bookmark ?? null);

const currentChapter = computed(() => {
  if (bookmark.value) {
    // Le backend retourne normalement le chapitre du dernier bookmark enregistré dans chapter_number
    const foundChapter = project.value?.chapters?.find(c => c.order === bookmark.value?.chapter_number);
    if (foundChapter) return foundChapter;
  }
  return project.value?.chapters?.[0] ?? null;
});

const readingButtonLabel = computed(() => {
  if (bookmark.value) {
    if ((!bookmark.value.chapter_number || bookmark.value.chapter_number === 1) && bookmark.value.words_read === 0) {
      return "Commencer la lecture";
    }
    return "Reprendre la lecture";
  }
  return "Commencer la lecture";
});

// On initialise isFavorite à partir de la prop is_favorite du projet si elle est présente
const {
  isFavorite,
  isPending: isFavoritePending,
  toggleFavorite,
} = handleProjectFavorite(project.value?.is_favorite ?? false, props.refreshNuxtData);

// On met à jour isFavorite si project change (utile si refreshNuxtData est appelé)
watch(() => project.value?.is_favorite, (newValue) => {
  if (newValue !== undefined) {
    isFavorite.value = newValue;
  }
});

// load comments
const comments = ref<CommentData[]>([]);
const commentsPage = ref(1);
const commentsLastPage = ref(1);
const isCommentsLoading = ref(false);

async function loadComments(page = 1) {
  if (!project.value || isCommentsLoading.value) return;
  isCommentsLoading.value = true;
  try {
    const response = await $api<{
      data: CommentData[],
      meta: { current_page: number, last_page: number }
    }>(`/api/project/${project.value.uuid}/comments`, {
      params: {page},
    });
    if (page === 1) {
      comments.value = response.data;
    } else {
      comments.value = [...comments.value, ...response.data];
    }
    commentsPage.value = response.meta.current_page;
    commentsLastPage.value = response.meta.last_page;
  } catch (e) {
    console.error("Erreur lors du chargement des commentaires", e);
  } finally {
    isCommentsLoading.value = false;
  }
}

if (project.value && project.value.comments_count > 0) {
  loadComments();
}

const tabs = [
  {
    label: "Synopsis",
    value: "abstract",
    slot: "abstract",
  },
  {
    label: "Chapitres",
    value: "chapters",
    slot: "chapters",
  },
  {
    label: "Auteur•e",
    value: "authors",
    slot: "authors",
  },
  {
    label: `Retours lecteurs (${project.value?.comments_count})`,
    value: "comments",
    slot: "comments",
  },
];

const selectedTab = computed({
  get() {
    return (route.query.tab as string) || (tabs[0]?.value as string);
  },
  set(value) {
    router.replace({
      query: {
        ...route.query,
        tab: value,
      },
    });
  },
});
</script>

<template>
  <UPage id="pagePage" v-if="project" class="bg-white">
    <UContainer>
      <div id="pageHeader" class="py-8">
        <div class="flex flex-col gap-4 md:gap-8">
          <div class="flex justify-between gap-4 md:gap-8">
            <div class="flex flex-col justify-between space-y-4">
              <h1>{{ project.title }}</h1>
              <p class="text-sm md:text-xl">{{ project.pitch }}</p>
            </div>
            <div class="hidden md:block self-stretch h-auto border-l-2 border-primary"/>
            <div>
              <img v-if="project.image?.thumbnail"
                   :src="project.image?.thumbnail"
                   :alt="project.title"
                   class="rounded-xs image-project max-w-none w-[120px] md:w-[264px]"/>
              <USkeleton v-else class="image-project w-[120px] md:w-[264px]"/>
            </div>
          </div>

          <div
              class="flex flex-col items-start w-full lg:flex-row lg:justify-between lg:items-center gap-4 text-sm border-t-2 border-b-2 border-primary py-4 md:py-8">

            <div class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <div v-if="project.authors && project.authors.length" v-for="author in project.authors" :key="author.email"
                   class="flex items-baseline gap-2 md:border-r-2 border-primary pr-4 text-xl font-bold leading-none">
                <NuxtLink :to="`/author/${author.uuid}`" class="whitespace-nowrap hover:underline">{{ author.name }}</NuxtLink>
              </div>

              <div class="flex gap-x-1 md:gap-x-4 text-gray-400 items-center">
                <span class="whitespace-nowrap">{{ project.readers_count }} {{ pluralize(project.readers_count, "lecteur", "lecteurs") }}</span>
                <span class="whitespace-nowrap before:content-['•'] before:mr-1 md:before:mr-4">{{
                    chaptersCountLabel(project.public_chapters_count)
                  }}</span>
                <span v-if="project.public_chapters_count > 0" class="whitespace-nowrap before:content-['•'] before:mr-1 md:before:mr-4">{{
                    formatReadingTime(project.estimated_reading_time)
                  }}</span>
                <span class="whitespace-nowrap before:content-['•'] before:mr-1 md:before:mr-4">{{ projectStatusLabel(project.status) }}</span>
                <div v-if="project.spice_level" class="whitespace-nowrap before:content-['•'] before:mr-1 md:before:mr-4 flex">
                  <Tooltip :text="spiceLevelLabel(project.spice_level-1)">
                    <div>
                      <UIcon v-if="project.spice_level > 0" v-for="i in project.spice_level" name="i-custom-spicy" class="size-5"></UIcon>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div class="flex justify-between w-full">
              <div v-if="project.genres" class="flex items-center gap-2 flex-wrap">
                <div v-for="genre in project.genres" :key="genre.slug" class="flex gap-2">
                  <BadgeGenre :genre="genre"/>
                  <template v-for="subGenre in genre.sub_genres" :key="subGenre.slug">
                    <BadgeGenre :genre="subGenre"/>
                  </template>
                </div>
              </div>


              <div class="flex justify-between items-center">
                <div class="flex gap-x-1">
                  <UButton v-if="isTheAuthor" size="sm" :to="`/dashboard/projects/${project.uuid}`" color="secondary" variant="solid">Modifier
                  </UButton>

                  <div class="flex items-center">
                    <Tooltip text="Partager">
                      <ShareProject
                          v-if="project"
                          :url="projectUrl"
                          :title="project.title"
                          :description="project.pitch"
                      />
                    </Tooltip>
                  </div>

                  <div v-if="appUser && canRead">
                    <Tooltip :text="isFavorite ? 'Retirer des favoris':'Ajouter aux favoris'">
                      <UButton
                          :icon="isFavorite ? 'i-custom-bookmark-solid' : 'i-heroicons-bookmark'"
                          variant="ghost"
                          size="md"
                          @click.prevent="toggleFavorite(project.uuid)"
                          :disabled="isFavoritePending"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div class="flex w-full items-baseline gap-x-4">
            <div v-if="project.chapters && project.chapters.length > 0 && (canRead || (project.chapters[0] && project.chapters[0].order === 1))"
                 class="w-full text-center md:w-auto">
              <UButton :label="readingButtonLabel"
                       color="primary"
                       variant="solid"
                       size="xl"
                       trailing-icon="i-heroicons-arrow-long-right"
                       :to="`/project/${projectUuid}/read`"
                       class="w-full justify-center mb-2"></UButton>
              <p v-if="currentChapter" class="text-gray-400">
                Chapitre {{ currentChapter.order }} - {{ formatReadingTime(currentChapter.estimated_reading_time) }}
              </p>
            </div>
            <!-- TODO tendances, flags -->
          </div>
        </div>
      </div>

      <UPageBody id="pageBody" class="mt-4">
        <div class="flex flex-col lg:flex-row gap-8">
          <div class="flex-8/12 lg:border-r-2 lg:border-primary lg:pr-8">
            <UTabs v-model="selectedTab" :items="tabs" variant="link" color="primary">
              <template #abstract>
                <div class="mt-6 leading-8" v-html="nl2br(project.abstract)"></div>
              </template>
              <template #chapters>
                <div class="mt-4">
                  <ol v-if="project.chapters && project.chapters.length">
                    <li v-for="chapter in project.chapters" :key="chapter.uuid" class="border-b border-gray-100 last:border-none md:border-0 py-3">
                      <div class="flex justify-between items-start">
                        <ULink :to="`/project/${projectUuid}/read?chapter=${chapter.order}`"
                               class="flex flex-col md:flex-row gap-x-4 gap-y-2 text-primary">
                          <template v-if="!chapter.title || chapter.title.length === 0">
                            <span class="whitespace-nowrap">{{ "Chapitre " + chapter.order }}</span>
                          </template>
                          <span>{{ chapter.title }}</span>
                        </ULink>
                        <div class="flex items-center gap-x-1">
                          <Tooltip text="Déjà lu" v-if="chapter.user_progress?.fully_read">
                            <UIcon name="i-heroicons-check-circle"></UIcon>
                          </Tooltip>
                          <Tooltip text="Dans vos favoris">
                            <UIcon v-if="chapter.is_favorite" name="i-custom-bookmark-solid"></UIcon>
                          </Tooltip>
                          <span class="whitespace-pre text-gray-400">{{ formatReadingTime(chapter.estimated_reading_time) }}</span>
                        </div>
                      </div>
                    </li>
                  </ol>
                  <p v-else>Aucun chapitre pour le moment.</p>
                </div>
              </template>
              <template #authors>
                <div class="mt-6">
                  <div v-for="author in project.authors" :key="author.uuid">
                    <template v-if="author.profiles?.author">
                      <div class="flex items-center gap-4 mb-6">
                        <UAvatar :src="author.profiles?.author?.picture" :alt="author.name" size="md"/>
                        <span class="font-bold">{{ author.name }}</span>
                      </div>
                      <div class="leading-8 mb-8">
                        {{ author.profiles.author.bio }}
                      </div>
                      <UButton :to="`/author/${author.uuid}`">Consulter le profil de l'auteur•e</UButton>
                    </template>
                  </div>
                </div>
              </template>
              <template #comments>
                <div class="flex flex-col gap-4 mt-4">
                  <div class="flex justify-between">
                    <p>
                      <strong class="mr-2">Commentaires</strong>
                      <UBadge variant="subtle" color="primary" size="xs">{{ project.comments_count }}</UBadge>
                    </p>
                  </div>
                  <div v-if="comments.length > 0" class="flex flex-col gap-4">
                    <ProjectComment
                        v-for="comment in comments"
                        :key="comment.id"
                        :comment="comment"
                        @deleted="(id) => comments = comments.filter(c => c.id !== id)"
                        :show-context="true"
                    />

                    <div v-if="commentsPage < commentsLastPage" class="flex justify-center mt-4">
                      <UButton
                          label="Charger les commentaires précédents"
                          :loading="isCommentsLoading"
                          @click="loadComments(commentsPage + 1)"
                      />
                    </div>
                  </div>
                  <p v-else-if="!isCommentsLoading">Aucun commentaire pour le moment.</p>
                  <div v-if="isCommentsLoading && comments.length === 0" class="flex flex-col gap-4">
                    <USkeleton v-for="i in 3" :key="i" class="h-24 w-full"/>
                  </div>
                </div>
              </template>
            </UTabs>
          </div>
          <div class="flex-4/12 space-y-8 pt-8 lg:pt-0 border-t border-b-gray-300 lg:border-none">
            <div v-if="project.top_emotions.length > 0">
              <h3 class="text-h5 underline mb-4">Ce que tu vas ressentir</h3>
              <ul class="flex gap-2 flex-wrap">
                <li v-for="emotion in project.top_emotions">
                  <UBadge variant="outline" color="neutral" class="rounded-xs border-1 border-gray-200 flex items-center gap-x-2">
                    <UIcon :name="emotion.icon" class="size-5"/>
                    <span>{{ emotion.count }}</span><span>{{ emotion.title }}</span>
                  </UBadge>
                </li>
              </ul>
            </div>
            <SupportProjectCard :project="project" v-if="appUser && userHasRole(appUser, 'reader')"></SupportProjectCard>
            <CollaborationRequestCard :project="project" v-if="appUser && userHasRole(appUser, 'reader')"></CollaborationRequestCard>
            <div v-if="project.tropes && project.tropes.length > 0">
              <h3 class="text-h5 underline mb-4">Tropes</h3>
              <ul class="flex gap-2 flex-wrap">
                <li v-for="trope in project.tropes">
                  <UBadge size="xs">{{ trope.label }}</UBadge>
                </li>
              </ul>
            </div>
            <div v-if="project.spice_level > 0">
              <h3 class="text-h5 underline mb-4">Niveau de spice</h3>
              <div class="flex gap-x-2 items-center">
                <div class="flex items-center">
                  <UIcon v-if="project.spice_level > 0" v-for="i in project.spice_level" name="i-custom-spicy" class="size-5"></UIcon>
                </div>
                <span>{{ spiceLevelLabel(project.spice_level - 1) }}</span>
              </div>
            </div>
            <p v-if="project.explicit_content" class="text-sm text-gray-400">Contenu explicite 18+</p>
            <div v-if="project.copyright">
              <USeparator class="mb-4"></USeparator>
              <div v-html="nl2br(project.copyright)"></div>
            </div>
          </div>
        </div>
      </UPageBody>
    </UContainer>
  </UPage>
</template>
